'use client'

import { isSupportOutreach, showsCustomerClosure, customerVisibleMessages } from '@/core/utils/support-presentation'
import { useCallback, useEffect, useRef, useState } from 'react'
import { PeerMessageBlockButton } from './peer-message-block-button'
import { AvatarCircle } from './avatar-circle'
import Link from 'next/link'
import { observer } from 'mobx-react-lite'
import {
  ArrowLeft,
  Send,
  Paperclip,
  X as XIcon,
  Film,
  FileIcon,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  CheckCircle2,
  Package,
  Tag,
  MoreHorizontal,
} from 'lucide-react'
import {
  ConversationsRepository,
  Conversation,
  ConversationMessage,
  MessageAttachment,
  PeerMessagingState,
} from '@/core/repositories/conversations'
import { AppStore } from '@/core/store'
import { useConversationSocket } from './use-conversation-socket'
import { usePathname } from 'next/navigation'
import { linkifyOrderNumbers } from '@/utils/linkify-orders'
import {
  type MessageReference,
  bodyWithReferenceSummary,
  isListingReference,
  parseMessageReference,
  referenceEyebrow,
  referenceFacts,
  referenceHref,
  referencePriceFacts,
  referenceThumbnails,
  referenceTitle,
  stripReferenceSummary,
} from '@/core/utils/message-references'

/** Seed for draft (not-yet-created) listing chats. The thread renders the
 *  listing context + composer with an empty message list and only creates the
 *  conversation on the first send — so opening "Message" never persists an
 *  empty thread in the inbox. */
export interface ListingChatDraft {
  listingId: string
  sellerId?: string | null
  sellerName?: string | null
  sellerImageUrl?: string | null
  title?: string | null
  thumbnailPath?: string | null
}

interface ThreadProps {
  /** Existing conversation to open. Omit when seeding a `draft`. */
  id?: string
  /** Draft mode: no conversation exists yet (see ListingChatDraft). */
  draft?: ListingChatDraft
  /** Fired once a draft's first send creates the real conversation, so the
   *  embedding surface can switch from draft → thread view. */
  onCreated?: (conv: Conversation) => void
  /** Rendered inside the desktop split shell or the mobile single-column.
   *  When true, the component fills its container (height:100%) and the
   *  header swaps the standalone back-button for a close (X) + expand
   *  button pair anchored to the top-right. */
  embedded?: boolean
  /** In embedded mode, called when the user closes/dismisses the thread.
   *  Desktop: deselect the conversation. Mobile: pop back to /inbox. */
  onClose?: () => void
  /** Listing / order card staged in the composer when the thread opens, so
   *  the item the user came from is attached to their next message. One
   *  thread per pair means the item must travel with the message. */
  initialReference?: MessageReference | null
}

/** The card a draft composer shows before the server has built the full one. */
function draftReference(draft: ListingChatDraft): MessageReference {
  return {
    kind: 'listing',
    entityId: draft.listingId,
    listingId: draft.listingId,
    displayTitle: draft.title ?? null,
    thumbnailPaths: draft.thumbnailPath ? [draft.thumbnailPath] : [],
  }
}

/** Synthesize a minimal peer Conversation so the thread renders its header +
 *  listing context card before the real conversation exists. Replaced by the
 *  server row on first send. */
function buildDraftConversation(draft: ListingChatDraft): Conversation {
  const now = new Date().toISOString()
  return {
    id: '',
    type: 'peer',
    status: 'open',
    category: null,
    lane: null,
    priority: 'low',
    assignedAdminId: null,
    bannerCtaLabel: null,
    bannerCtaUrl: null,
    bannerColor: null,
    rating: null,
    ratingComment: null,
    ratedAt: null,
    ratingDismissedAt: null,
    contextListingId: draft.listingId,
    contextOrderId: null,
    listingSnapshot: { title: draft.title ?? undefined, thumbnailUrl: draft.thumbnailPath ?? undefined },
    orderSnapshot: null,
    closedAt: null,
    closedByUserId: null,
    snoozedUntil: null,
    previousConversationId: null,
    lastMessageAt: now,
    lastUserMessageAt: null,
    lastAdminMessageAt: null,
    createdAt: now,
    updatedAt: now,
    participants: draft.sellerId
      ? [
          {
            id: 'draft-seller',
            conversationId: '',
            userId: draft.sellerId,
            role: 'seller',
            unreadCount: 0,
            lastReadAt: null,
            mutedAt: null,
            user: {
              id: draft.sellerId,
              displayName: draft.sellerName ?? null,
              profileImagePath: draft.sellerImageUrl ?? null,
            },
          },
        ]
      : [],
    messages: [],
    contextOrder: null,
    contextListing: {
      id: draft.listingId,
      title: draft.title ?? null,
      thumbnailPath: draft.thumbnailPath ?? null,
      sellerId: draft.sellerId ?? null,
    },
    metadata: null,
  }
}

export const InboxThreadClient = observer(
  ({ id: propId, draft, onCreated, embedded, onClose, initialReference }: ThreadProps) => {
  // The conversation id can start null (draft mode) and become real on the
  // first send. Everything below keys off `convId`, not the prop.
  const [convId, setConvId] = useState<string | null>(propId ?? null)
  const [conv, setConv] = useState<Conversation | null>(
    draft ? buildDraftConversation(draft) : null
  )
  const [peerState, setPeerState] = useState<PeerMessagingState | null>(null)
  const [messages, setMessages] = useState<ConversationMessage[]>([])
  const [loading, setLoading] = useState(!!propId)
  const [error, setError] = useState<string | null>(null)
  const [reply, setReply] = useState('')
  const [replyAttachments, setReplyAttachments] = useState<MessageAttachment[]>([])
  /** Card attached to the next message. Seeded by the entry point (a listing
   *  or order page); cleared on send or when the user removes it. */
  const [pendingReference, setPendingReference] = useState<MessageReference | null>(
    () => initialReference ?? (draft ? draftReference(draft) : null)
  )
  const [uploading, setUploading] = useState(false)
  const [sending, setSending] = useState(false)
  /** Snapshot of my last-read-at *before* this load fired markRead, so the
   *  unread divider stays anchored even after the server marks everything
   *  read. Captured once per thread mount, stays stable across replies. */
  const [initialLastReadAt, setInitialLastReadAt] = useState<string | null>(null)
  /** When the user has scrolled away from the bottom and a new socket
   *  message arrives, we keep them anchored and surface a "↓ N new" pill
   *  instead of yanking the scroll. */
  const [unreadBelow, setUnreadBelow] = useState(0)
  /** Other participants currently typing. Keyed by userId so re-keystrokes
   *  refresh the entry instead of stacking. Auto-pruned every second in
   *  case the "stopped" event is lost. */
  const [typers, setTypers] = useState<
    Record<string, { name: string | null; lastSeenAt: number }>
  >({})
  /** Attachment group + index currently open in the lightbox overlay. The
   *  group is the message's full attachment array so prev/next can navigate
   *  within it. Null when closed. */
  const [lightbox, setLightbox] = useState<{
    attachments: MessageAttachment[]
    index: number
  } | null>(null)
  /** Per-message client-side status. Only tracks optimistic temp messages
   *  while they're in-flight or after failure — once swapped for the real
   *  server message, the entry is cleared and the message is treated as
   *  successfully sent. */
  const [pendingStatus, setPendingStatus] = useState<
    Record<string, 'sending' | 'failed'>
  >({})
  const scrollRef = useRef<HTMLDivElement>(null)
  const isAtBottomRef = useRef(true)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const isTypingRef = useRef(false)
  const stopTypingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Set when a draft's first send creates the conversation locally, so the
  // convId-change-driven load() skips its fetch (we already have the row +
  // the optimistic message in state — refetching would flicker / drop it).
  const justCreatedRef = useRef(false)
  const viewerId = AppStore.accountData?.id ?? null
  const peerAccountId = conv?.participants?.find(p => p.userId && p.userId !== viewerId)?.userId
  // A listing draft can arrive before its seller identity is loaded. Keep that
  // existing create-on-send path usable; the server checks blocks when creating
  // and sending. Once the peer is known, wait for the account-pair state.
  const peerMessagingUnavailable = conv?.type === 'peer' && (peerAccountId
    ? !peerState || peerState.messagingBlocked
    : conv.messagingBlocked === true || conv.blockedByMe === true)

  const load = useCallback(async () => {
    const cid = convId
    // Draft mode (no conversation yet) — nothing to fetch. The seeded stub
    // conv + empty message list render the context header and composer.
    if (!cid) {
      setLoading(false)
      return
    }
    // Just created from a draft's first send — we already have the row and the
    // optimistic message in state; skip the refetch so it doesn't flicker.
    if (justCreatedRef.current) {
      justCreatedRef.current = false
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      const [c, msgs] = await Promise.all([
        ConversationsRepository.getById(cid),
        ConversationsRepository.getMessages(cid),
      ])
      setConv(c)
      setMessages([...msgs.messages].reverse())
      setError(null)
      // Snapshot my pre-mark-read timestamp once per mount so the unread
      // divider has a stable anchor even after the server clears the badge.
      const meHere = viewerId ? c.participants?.find((p) => p.userId === viewerId) : null
      setInitialLastReadAt((prev) => prev ?? meHere?.lastReadAt ?? null)
      // Flip loading false synchronously WITH the data set so React commits
      // both in one render. Otherwise the intermediate `loading=true,
      // messages=populated` render runs the [messages] effect with the
      // scroll element not yet in the DOM (we render "Loading..." while
      // loading is true), and we miss the initial scroll-to-bottom.
      setLoading(false)
      // markRead is fire-and-forget — no need to gate the UI on the
      // server's ack of "I read this".
      ConversationsRepository.markRead(cid).catch(() => {})

      // CSAT prompt is rendered inline in the thread (not a modal) once the
      // conversation closes — see <InlineRatingCard /> render block.
    } catch (err: any) {
      setError(err.message || 'Failed to load')
      setLoading(false)
    }
  }, [convId])

  useEffect(() => {
    load()
  }, [load])

  /** Auto-scroll on new messages.
   *
   *  On first messages arrival we open in "stick to bottom" mode and wire a
   *  ResizeObserver on the scroll content. Every time the content grows
   *  (text rendered, images load late, embeds resize), we re-pin to bottom.
   *  Stick mode releases as soon as the user actually scrolls up, or after
   *  a hard timeout backstop.
   *
   *  After the initial settle, only scroll if the user is already near the
   *  bottom; if they've scrolled up to read history, anchor them in place
   *  and let the "↓ N new" pill surface instead. */
  const initialScrollDoneRef = useRef(false)
  const stickToBottomRef = useRef(true)
  useEffect(() => {
    const el = scrollRef.current
    if (!el || messages.length === 0) return

    if (!initialScrollDoneRef.current) {
      initialScrollDoneRef.current = true
      stickToBottomRef.current = true
      const stick = () => {
        if (!stickToBottomRef.current) return
        // If the user scrolled away during the settle window, hand
        // control back to them — don't yank the viewport to bottom.
        if (!isAtBottomRef.current) {
          stickToBottomRef.current = false
          return
        }
        el.scrollTop = el.scrollHeight
      }
      // Sync pin + a few frame-aligned replays for the cheap cases.
      stick()
      requestAnimationFrame(stick)
      const t1 = setTimeout(stick, 60)
      // ResizeObserver re-pins whenever the inner content grows (image
      // loads are the typical culprit — they go 0px → real height).
      const ro = new ResizeObserver(stick)
      Array.from(el.children).forEach((c) => ro.observe(c))
      ro.observe(el)
      // Release stick mode after a generous window so user scrolling
      // can take over. 3s comfortably covers most CDN-served thumbnails.
      const release = setTimeout(() => {
        stickToBottomRef.current = false
        ro.disconnect()
      }, 3000)
      return () => {
        clearTimeout(t1)
        clearTimeout(release)
        ro.disconnect()
      }
    }
    if (isAtBottomRef.current) {
      el.scrollTop = el.scrollHeight
    }
  }, [messages])

  const onMessagesScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100
    isAtBottomRef.current = atBottom
    if (atBottom && unreadBelow > 0) setUnreadBelow(0)
  }, [unreadBelow])

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
    isAtBottomRef.current = true
    setUnreadBelow(0)
  }, [])

  const refreshStatus = useCallback(async () => {
    if (!convId) return
    try {
      const latest = await ConversationsRepository.getById(convId)
      setConv(prev => prev?.id === latest.id ? { ...prev, ...latest } : prev)
    } catch {
      // Keep the open thread usable during a temporary connection failure.
    }
  }, [convId])

  // ── Live updates: subscribe to socket events for THIS conversation ──
  useConversationSocket({
    onRefresh: refreshStatus,
    onNewMessage: (e) => {
      if (e.conversationId !== convId) return
      // Skip our own messages — the optimistic + API-response flow already
      // handles them, and we'd otherwise double-render.
      if (e.message.senderUserId && e.message.senderUserId === viewerId) return
      setMessages((prev) => {
        if (prev.some((m) => m.id === e.message.id)) return prev
        return [...prev, e.message]
      })
      // User is actively viewing — mark read and broadcast our own read
      // receipt so the other party's "Seen" indicator updates.
      ConversationsRepository.markRead(e.conversationId).catch(() => {})
      if (!isAtBottomRef.current) {
        setUnreadBelow((n) => n + 1)
      }
    },
    onUpdated: (e) => {
      if (e.conversationId !== convId) return
      setConv((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          ...(e.status !== undefined ? { status: e.status } : {}),
          ...(e.assignedAdminId !== undefined
            ? { assignedAdminId: e.assignedAdminId }
            : {}),
          ...(e.category !== undefined ? { category: e.category } : {}),
          ...(e.lane !== undefined ? { lane: e.lane } : {}),
          ...(e.closedAt !== undefined ? { closedAt: e.closedAt } : {}),
        }
      })
    },
    onParticipantRead: (e) => {
      if (e.conversationId !== convId) return
      setConv((prev) => {
        if (!prev?.participants) return prev
        return {
          ...prev,
          participants: prev.participants.map((p) =>
            p.userId === e.userId ? { ...p, lastReadAt: e.lastReadAt } : p
          ),
        }
      })
    },
    onTyping: (e) => {
      if (e.conversationId !== convId) return
      if (e.userId === viewerId) return
      setTypers((prev) => {
        const next = { ...prev }
        if (e.isTyping) {
          next[e.userId] = { name: e.userName, lastSeenAt: Date.now() }
        } else {
          delete next[e.userId]
        }
        return next
      })
    },
  })

  // Prune stale typing entries every second so a lost "stopped" event
  // doesn't pin "Jenny is typing…" forever. 5s window matches the debounce
  // budget on the sender side with a small grace.
  useEffect(() => {
    if (Object.keys(typers).length === 0) return
    const t = setInterval(() => {
      const cutoff = Date.now() - 5000
      setTypers((prev) => {
        const next = { ...prev }
        let changed = false
        for (const k of Object.keys(next)) {
          if (next[k].lastSeenAt < cutoff) {
            delete next[k]
            changed = true
          }
        }
        return changed ? next : prev
      })
    }, 1000)
    return () => clearInterval(t)
  }, [typers])

  // Helpers for outbound typing pings. Debounced: send "true" once when the
  // user starts, and "false" 3s after the last keystroke. Sends "false"
  // immediately on send / blur / unmount.
  const stopTypingNow = useCallback(() => {
    if (stopTypingTimerRef.current) {
      clearTimeout(stopTypingTimerRef.current)
      stopTypingTimerRef.current = null
    }
    if (isTypingRef.current) {
      isTypingRef.current = false
      if (convId) ConversationsRepository.setTyping(convId, false).catch(() => {})
    }
  }, [convId])

  const onComposerChange = useCallback(
    (text: string) => {
      setReply(text)
      const hasText = text.trim().length > 0
      if (!hasText) {
        stopTypingNow()
        return
      }
      if (peerMessagingUnavailable) return
      // No typing pings in draft mode (no conversation to broadcast on yet).
      if (!convId) return
      if (!isTypingRef.current) {
        isTypingRef.current = true
        ConversationsRepository.setTyping(convId, true).catch(() => {})
      }
      if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current)
      stopTypingTimerRef.current = setTimeout(() => {
        isTypingRef.current = false
        if (convId) ConversationsRepository.setTyping(convId, false).catch(() => {})
        stopTypingTimerRef.current = null
      }, 3000)
    },
    [convId, stopTypingNow, peerMessagingUnavailable]
  )

  // Stop typing on unmount or when the open thread changes (key={selectedId}
  // remounts handle the latter, but be defensive).
  useEffect(() => {
    return () => {
      stopTypingNow()
    }
  }, [stopTypingNow])

  /** Inner send used by both the composer Send button and the per-message
   *  retry tap. When `existingTempId` is passed, we update the in-stream
   *  message in place (used by retry); otherwise we create a fresh temp and
   *  append it. */
  const doSend = useCallback(
    async (
      text: string,
      atts: MessageAttachment[],
      options?: { existingTempId?: string; reference?: MessageReference | null }
    ) => {
      if (peerMessagingUnavailable) return
      let reference = options?.reference ?? null
      if (!text && atts.length === 0 && !reference) return

      // Draft mode: the conversation doesn't exist yet. Create it on this
      // first send (get-or-create on the backend), then proceed exactly like
      // a normal send. Skip the convId-change reload so we don't drop the
      // optimistic bubble.
      let targetId = convId
      if (!targetId) {
        if (!draft) return
        setSending(true)
        try {
          const created = await ConversationsRepository.createPeerForListing(draft.listingId)
          targetId = created.id
          justCreatedRef.current = true
          // The server's card carries price, status, and photo; the draft
          // only knew the title. A card the user removed stays removed.
          if (reference && created.sharedContext) reference = created.sharedContext
          setConv(created)
          setConvId(created.id)
          onCreated?.(created)
        } catch (err: any) {
          setSending(false)
          setError(
            err?.message === 'Blocked'
              ? "You can't message this seller."
              : err.message || 'Failed to send'
          )
          return
        }
      }

      const tempId =
        options?.existingTempId ||
        `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      // On the wire the card's summary prefixes the body, so clients that
      // predate cards still read the context. Card-aware clients strip it.
      const wireBody = reference ? bodyWithReferenceSummary(text, reference) : text
      const metadata = reference ? { sharedContext: reference } : null

      if (!options?.existingTempId) {
        // Populate `sender` from the local AppStore so the optimistic
        // bubble renders with the viewer's actual name + avatar instead
        // of falling back to the generic "You" + initial circle. The full
        // picture URL works because resolveAssetUrl/profileImageUrl pass
        // http(s) values through unchanged.
        const me = AppStore.accountData
        const tempMessage: ConversationMessage = {
          id: tempId,
          conversationId: targetId,
          senderType: 'user',
          senderUserId: viewerId,
          body: wireBody || null,
          attachments: atts.length > 0 ? atts : null,
          metadata,
          createdAt: new Date().toISOString(),
          editedAt: null,
          deletedAt: null,
          sender: me
            ? {
                id: me.id,
                name: me.name || undefined,
                displayName: me.displayName ?? undefined,
                profileImagePath: me.picture || null,
              }
            : undefined,
        }
        setMessages((prev) => [...prev, tempMessage])
      }
      setPendingStatus((prev) => ({ ...prev, [tempId]: 'sending' }))
      setSending(true)
      stopTypingNow()

      try {
        const realMessage = await ConversationsRepository.sendMessage(targetId, {
          body: wireBody || undefined,
          attachments: atts.length > 0 ? atts : undefined,
          metadata: metadata ?? undefined,
        })
        setMessages((prev) =>
          prev.map((m) => (m.id === tempId ? realMessage : m))
        )
        void refreshStatus()
        setPendingStatus((prev) => {
          const next = { ...prev }
          delete next[tempId]
          return next
        })
      } catch (err: any) {
        // Keep the temp in place so the user can see what failed and tap
        // to retry. The Failed caption + Retry handler live on the row.
        setPendingStatus((prev) => ({ ...prev, [tempId]: 'failed' }))
        setError(err.message || 'Failed to send')
      } finally {
        setSending(false)
      }
    },
    [convId, draft, onCreated, viewerId, stopTypingNow, peerMessagingUnavailable, refreshStatus]
  )

  const send = async () => {
    if (peerMessagingUnavailable) return
    const text = reply.trim()
    const atts = replyAttachments
    const reference = pendingReference
    if (!text && atts.length === 0 && !reference) return
    setReply('')
    setReplyAttachments([])
    setPendingReference(null)
    await doSend(text, atts, { reference })
  }

  const retrySend = useCallback(
    async (tempId: string) => {
      const m = messages.find((x) => x.id === tempId)
      if (!m) return
      const reference = parseMessageReference(m.metadata)
      await doSend(stripReferenceSummary(m.body, reference), m.attachments || [], {
        existingTempId: tempId,
        reference,
      })
    },
    [messages, doSend]
  )

  const onPickFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    const remaining = 10 - replyAttachments.length
    if (remaining <= 0) return
    const picked = Array.from(files).slice(0, remaining)
    setUploading(true)
    try {
      const uploaded = await ConversationsRepository.uploadAttachments(picked)
      setReplyAttachments((prev) => [...prev, ...uploaded])
    } catch (err: any) {
      setError(err.message || 'Upload failed')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const removeAttachment = (idx: number) => {
    setReplyAttachments((prev) => prev.filter((_, i) => i !== idx))
  }

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: 'var(--font_2)' }}>
        Loading…
      </div>
    )
  }
  if (error || !conv) {
    return (
      <div style={{ padding: 40, color: 'var(--ios-red)', fontSize: 14 }}>
        {error || 'Not found'}
      </div>
    )
  }

  // Build a small avatar block for the header so the thread feels like a
  // proper chat surface (Google Chat / Messages pattern).
  const otherForHeader = conv.participants?.find((p) => p.userId && p.userId !== viewerId)
    ?? conv.participants?.[0]

  const headerLabel =
    conv.type === 'support'
      ? 'MarkitMix Support'
      : otherForHeader?.user?.displayName ||
        otherForHeader?.user?.name ||
        'Conversation'

  // Registered (legal) name shown in parens after the display name ONLY when
  // the viewer is the seller in this conversation — so a seller can match a
  // message to an order. A buyer never sees a seller's legal name. Skipped
  // when the registered name matches the display name (no redundant parens).
  const viewerRoleInConvo = conv.participants?.find((p) => p.userId === viewerId)?.role
  const otherRegisteredName = otherForHeader?.user?.name?.trim() || ''
  const showOtherRegistered =
    conv.type !== 'support' &&
    viewerRoleInConvo === 'seller' &&
    !!otherForHeader?.user?.displayName &&
    !!otherRegisteredName &&
    otherRegisteredName.toLowerCase() !== (otherForHeader.user.displayName || '').toLowerCase()

  const headerAvatarUrl =
    conv.type === 'support'
      ? null
      : profileImageUrl(otherForHeader?.user?.profileImagePath ?? null)
  const headerInitial =
    conv.type === 'support' ? 'M' : (headerLabel.trim()[0] || '?').toUpperCase()
  const headerAvatarBg =
    conv.type === 'support' ? 'var(--primary)' : avatarColorFromName(headerLabel)

  // Online-presence: existing socket already populates AppStore.loggedInAccounts
  // (see SocketController allConnectedAccounts handler). For peer threads we
  // surface a green dot when the OTHER party is currently connected. Support
  // threads always show "MarkitMix Support" — never a presence dot.
  const otherIsOnline =
    conv.type === 'peer' &&
    !!otherForHeader?.userId &&
    AppStore.loggedInAccounts.includes(otherForHeader.userId)

  // Per-message status badges (Sending / Sent / Seen / Failed). Following
  // the Messenger / iMessage convention: at most one badge per spot, the
  // latest-of-mine message gets "Sending"/"Sent"/"Failed", and the latest
  // message any other participant has read past gets "Seen". When those
  // collide on the same message, "Seen" wins.
  const lastSeenMessageId = (() => {
    if (!conv.participants || !viewerId) return null
    const others = conv.participants.filter(
      (p) => p.userId && p.userId !== viewerId
    )
    if (others.length === 0) return null
    const latestReadMs = others.reduce((max, p) => {
      const t = p.lastReadAt ? new Date(p.lastReadAt).getTime() : 0
      return Math.max(max, t)
    }, 0)
    if (!latestReadMs) return null
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (!m.senderUserId || m.senderUserId !== viewerId) continue
      if (new Date(m.createdAt).getTime() <= latestReadMs) return m.id
    }
    return null
  })()

  const latestOfMineId = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (m.senderUserId && m.senderUserId === viewerId) return m.id
    }
    return null
  })()

  // Per-user role lookup so MessageRow can append "(seller)" next to the
  // appropriate name without doing the join every render.
  const participantRoleByUserId: Record<string, 'buyer' | 'seller' | 'support' | 'ai_agent'> = {}
  for (const p of conv.participants || []) {
    if (p.userId) participantRoleByUserId[p.userId] = p.role
  }

  const messageStatuses: Record<
    string,
    'sending' | 'sent' | 'seen' | 'failed'
  > = {}
  if (lastSeenMessageId) {
    // Suppress the Seen caption if the recipient has since replied — their
    // reply is itself the "they read it" signal, so showing Seen on an
    // older message is redundant noise.
    const seenIdx = messages.findIndex((m) => m.id === lastSeenMessageId)
    const hasReplyAfter =
      seenIdx >= 0 &&
      messages
        .slice(seenIdx + 1)
        .some((m) => m.senderUserId && m.senderUserId !== viewerId)
    if (!hasReplyAfter) messageStatuses[lastSeenMessageId] = 'seen'
  }
  if (latestOfMineId && messageStatuses[latestOfMineId] !== 'seen') {
    const ps = pendingStatus[latestOfMineId]
    if (ps === 'sending') messageStatuses[latestOfMineId] = 'sending'
    else if (ps === 'failed') messageStatuses[latestOfMineId] = 'failed'
    else messageStatuses[latestOfMineId] = 'sent'
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        // Both modes need a *fixed* height (not min-height) so the inner
        // flex:1 messages region can be capped and overflow-scrolled.
        // Without this the column grows past the viewport on a long thread,
        // pushing the composer below the fold.
        ...(embedded
          ? { height: '100%' }
          : { height: 'calc(100dvh - var(--header-height, 64px))' }),
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 16px',
          borderBottom: '1px solid var(--separator)',
          background: 'var(--background_1, var(--background_2))',
          flexShrink: 0,
        }}
      >
        {!embedded && (
          <Link
            href="/inbox"
            aria-label="Back to inbox"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              color: 'var(--font_1)',
              textDecoration: 'none',
              padding: 6,
              margin: -6,
            }}
          >
            <ArrowLeft size={20} />
          </Link>
        )}

        {/* Avatar (with online presence dot) */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <AvatarCircle
            url={headerAvatarUrl}
            initial={headerInitial}
            bgColor={headerAvatarBg}
            isSupport={conv.type === 'support'}
            size={36}
            fontSize={14}
          />
          {otherIsOnline && (
            <span
              aria-label="Active now"
              title="Active now"
              style={{
                position: 'absolute',
                bottom: -1,
                right: -1,
                width: 11,
                height: 11,
                borderRadius: '50%',
                background: 'var(--ios-green)',
                boxShadow: '0 0 0 2px var(--background_1, var(--background_2))',
              }}
            />
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--font_1)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              letterSpacing: '-0.01em',
            }}
          >
            {headerLabel}
            {showOtherRegistered && (
              <span style={{ fontWeight: 500, color: 'var(--font_3)' }}> ({otherRegisteredName})</span>
            )}
          </div>
          {(() => {
            // Header subtitle. Two cases:
            //   - Support thread: show the operator-set subject (from
            //     /console/support/compose) so the recipient sees what the
            //     thread is about without scrolling.
            //   - Peer thread: explicit role context ("Selling · <listing>")
            //     so the user knows which hat they're wearing.
            if (conv.type === 'support') {
              const s = (conv.metadata as { subject?: unknown } | null | undefined)?.subject
              if (typeof s !== 'string' || !s.trim()) return null
              return (
                <div
                  style={{
                    fontSize: 13,
                    color: 'var(--font_2)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontWeight: 500,
                  }}
                >
                  {s.trim()}
                </div>
              )
            }
            const myRole = conv.participants?.find(
              (p) => p.userId && p.userId === viewerId
            )?.role
            const isPeer = conv.type === 'peer'
            // Match the inbox row badge: a listing-pinned thread with no order
            // behind it is a pre-purchase question, not a buy/sell.
            const isOrderBacked = !!conv.contextOrderId
            let roleLabel: string | null = null
            let roleColor: string | null = null
            if (isPeer && !isOrderBacked) {
              roleLabel = 'Question'
              roleColor = 'var(--ios-blue)'
            } else if (isPeer && myRole === 'seller') {
              roleLabel = 'Selling'
              roleColor = 'var(--ios-green)'
            } else if (isPeer && myRole === 'buyer') {
              roleLabel = 'Buying'
              roleColor = 'var(--ios-blue)'
            }
            if (!roleLabel) return null
            return (
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--font_2)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {roleLabel && (
                  <span
                    style={{
                      color: roleColor || 'var(--font_2)',
                      fontWeight: 600,
                    }}
                  >
                    {roleLabel}
                  </span>
                )}
              </div>
            )
          })()}
        </div>
        {conv.type === 'peer' && otherForHeader?.userId && (
          <details className="relative shrink-0">
            <summary aria-label="Conversation options" className="flex min-h-11 min-w-11 cursor-pointer list-none items-center justify-center rounded-full text-[var(--font_1)] hover:bg-[var(--background_2)]">
              <MoreHorizontal size={20} />
            </summary>
            <div className="absolute right-0 top-full z-50 mt-2 w-64 rounded-xl border border-[var(--separator)] bg-[var(--background_4)] p-1.5">
              <PeerMessageBlockButton key={otherForHeader.userId} accountId={otherForHeader.userId} onStateChanged={setPeerState} />
            </div>
          </details>
        )}
        {conv.status !== 'closed' && conv.type === 'support' && !isSupportOutreach(conv) && (
          <div
            role="button"
            tabIndex={0}
            aria-label="Close this conversation"
            title="Closes the thread and lets you rate your experience. Replying any time will reopen it."
            onClick={async () => {
              // No native confirm() — friction we don't need. Reply auto-
              // reopens, and the inline CSAT card gives the user a natural
              // moment to reflect right after.
              if (!convId) return
              await ConversationsRepository.close(convId)
              await load()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                ;(e.currentTarget as HTMLElement).click()
              }
            }}
            className="cursor-pointer select-none"
            // iOS-native "borderless tinted" button — system color on text +
            // icon, no chrome. Apple separates color (which conveys meaning)
            // from container (which conveys affordance). The hover state is
            // a brief opacity dip, the way UIKit fades buttons on touch.
            // lineHeight: 1 prevents the text's inherited line-box from
            // sitting visually higher than the icon's geometric center.
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--ios-green)',
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1,
              padding: '0 6px',
              height: 28,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              letterSpacing: '-0.01em',
            }}
            onMouseEnter={(e) =>
              ((e.currentTarget as HTMLElement).style.opacity = '0.6')
            }
            onMouseLeave={(e) =>
              ((e.currentTarget as HTMLElement).style.opacity = '1')
            }
          >
            <CheckCircle2 size={14} strokeWidth={2} />
            Close conversation
          </div>
        )}
        {embedded && (
          <>
            {onClose && (
              <div
                role="button"
                tabIndex={0}
                aria-label="Close conversation"
                onClick={onClose}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onClose()
                  }
                }}
                className="cursor-pointer select-none"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  color: 'var(--font_2)',
                  transition: 'background 120ms, color 120ms',
                }}
                onMouseEnter={(e) => {
                  ;(e.currentTarget as HTMLElement).style.background =
                    'color-mix(in srgb, var(--font_1) 6%, transparent)'
                  ;(e.currentTarget as HTMLElement).style.color = 'var(--font_1)'
                }}
                onMouseLeave={(e) => {
                  ;(e.currentTarget as HTMLElement).style.background = 'transparent'
                  ;(e.currentTarget as HTMLElement).style.color = 'var(--font_2)'
                }}
              >
                <XIcon size={18} />
              </div>
            )}
          </>
        )}
      </div>

      {/* Banner for alert/warning */}
      {(conv.category === 'alert' || conv.category === 'warning') && (
        <div
          style={{
            padding: '10px 16px',
            background:
              conv.bannerColor === 'red'
                ? 'color-mix(in srgb, var(--ios-red) 12%, transparent)'
                : 'color-mix(in srgb, var(--ios-orange) 12%, transparent)',
            borderBottom: '1px solid var(--separator)',
            color: 'var(--font_1)',
            fontSize: 13,
          }}
        >
          <strong>{conv.category === 'alert' ? 'Action required.' : 'Account warning.'}</strong>
          {conv.bannerCtaLabel && conv.bannerCtaUrl && (
            <a
              href={conv.bannerCtaUrl}
              style={{ marginLeft: 8, color: 'var(--primary)', textDecoration: 'underline' }}
            >
              {conv.bannerCtaLabel}
            </a>
          )}
        </div>
      )}

      {/* Messages */}
      <div
        style={{
          position: 'relative',
          flex: 1,
          // Critical: in a flex column, default min-height is `auto` which
          // prevents this item from shrinking under its content. Without
          // this the messages section grows past the viewport and pushes
          // the composer off-screen.
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          ref={scrollRef}
          onScroll={onMessagesScroll}
          style={{
            flex: 1,
            // Same reason — without min-height: 0 the scroll container
            // can't shrink below its content, so `overflow: auto` never
            // engages.
            minHeight: 0,
            overflowY: 'auto',
            padding: '16px 0 12px',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--background_1, var(--background_2))',
          }}
        >
          {messages.length === 0 ? (
            <div style={{ color: 'var(--font_2)', fontSize: 14, textAlign: 'center', margin: 'auto' }}>
              No messages yet.
            </div>
          ) : (
            renderMessageStream(
              customerVisibleMessages(conv, messages),
              viewerId,
              initialLastReadAt,
              messageStatuses,
              participantRoleByUserId,
              (list, index) => setLightbox({ attachments: list, index }),
              retrySend
            )
          )}
        </div>

        {/* "↓ N new" pill — appears when a message arrives while the user is
            scrolled away from the bottom. Tap to jump down. */}
        {unreadBelow > 0 && (
          <div
            role="button"
            tabIndex={0}
            onClick={scrollToBottom}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                scrollToBottom()
              }
            }}
            className="cursor-pointer select-none"
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              bottom: 12,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'var(--primary)',
              color: 'var(--primary_text)',
              fontSize: 13,
              fontWeight: 600,
              padding: '6px 14px',
              borderRadius: 999,
              boxShadow: '0 2px 12px -2px rgba(0,0,0,0.18), 0 1px 2px rgba(0,0,0,0.06)',
            }}
          >
            ↓ {unreadBelow} new {unreadBelow === 1 ? 'message' : 'messages'}
          </div>
        )}
      </div>

      {/* Typing indicator — sits above the composer so it doesn't reflow
          message scroll. Visible only when at least one OTHER participant
          is currently typing. */}
      {Object.keys(typers).length > 0 && conv.status !== 'closed' && (
        <TypingPill typers={typers} />
      )}

      {/* Inline CSAT card — shown in the thread once a support conversation
          closes, until the user rates or dismisses. Replaces the old
          full-screen RatingModal so it feels like a message, not an
          interruption. */}
      {conv.type === 'support' &&
        showsCustomerClosure(conv) &&
        !conv.rating &&
        !conv.ratingDismissedAt && (
          <InlineRatingCard
            onSubmit={async (r, c) => {
              if (!convId) return
              await ConversationsRepository.submitRating(convId, r, c)
              // Optimistic update so the card disappears immediately while
              // the socket event for the system message catches up.
              setConv((prev) =>
                prev ? { ...prev, rating: r, ratingComment: c ?? null } : prev
              )
              await load()
            }}
            onDismiss={async () => {
              if (!convId) return
              await ConversationsRepository.dismissRatingPrompt(convId)
              setConv((prev) =>
                prev
                  ? { ...prev, ratingDismissedAt: new Date().toISOString() }
                  : prev
              )
            }}
          />
        )}

      {/* Composer — always visible. When the thread is closed, replying
          auto-reopens support conversations regardless of closure age.
          We surface that via the placeholder + a small CTA to start a new
          conversation for unrelated issues. */}
      {(() => {
        if (peerMessagingUnavailable) {
          return <div role="status" className="border-t border-[var(--separator)] bg-[var(--background_2)] px-4 py-4 text-sm text-[var(--font_2)]">
            {!peerState ? 'Open conversation options to check message settings.' : peerState.blockedByMe
              ? 'You blocked messages with this person. Open conversation options to unblock messages.'
              : 'Messaging is unavailable for this conversation.'}
          </div>
        }
        const isClosed = showsCustomerClosure(conv)
        const canSend =
          (!!reply.trim() || replyAttachments.length > 0 || !!pendingReference) && !sending
        return (
        <div
          style={{
            padding: '10px 12px',
            borderTop: '1px solid var(--separator)',
            background: 'var(--background_1, var(--background_2))',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {isClosed && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                fontSize: 12,
                color: 'var(--font_3)',
                padding: '0 4px',
              }}
            >
              <span>Conversation closed. Reply if you need more help.</span>
              <button
                type="button"
                onClick={() =>
                  window.dispatchEvent(new CustomEvent('mm-open-help-picker'))
                }
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--primary)',
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Start a new conversation
              </button>
            </div>
          )}
          {/* The listing / order card going out with this message */}
          {pendingReference && (
            <PendingReferenceChip
              reference={pendingReference}
              onRemove={() => setPendingReference(null)}
            />
          )}
          {/* Attachment chips above the textarea */}
          {replyAttachments.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(64px, 1fr))',
                gap: 6,
              }}
            >
              {replyAttachments.map((a, i) => (
                <ReplyAttachmentChip
                  key={`${a.assetId ?? a.url}-${i}`}
                  a={a}
                  onRemove={() => removeAttachment(i)}
                />
              ))}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*,.pdf,.txt,.rtf,.csv,.json,.md,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.pages,.numbers,.keynote,.zip"
            multiple
            onChange={(e) => onPickFiles(e.target.files)}
            style={{ display: 'none' }}
          />
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'flex-end',
            }}
          >
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || replyAttachments.length >= 10}
              aria-label="Attach a file"
              title="Attach a file"
              style={{
                background: 'transparent',
                border: '1px solid var(--separator)',
                color: 'var(--font_2)',
                width: 40,
                height: 40,
                borderRadius: '50%',
                cursor:
                  uploading || replyAttachments.length >= 10
                    ? 'not-allowed'
                    : 'pointer',
                opacity:
                  uploading || replyAttachments.length >= 10 ? 0.5 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Paperclip size={18} />
            </button>
            <textarea
              value={reply}
              onChange={(e) => onComposerChange(e.target.value)}
              onBlur={stopTypingNow}
              placeholder={
                uploading
                  ? 'Uploading…'
                  : isClosed
                  ? 'Reply if you need more help…'
                  : 'Type a message…'
              }
              rows={1}
              style={{
                flex: 1,
                borderRadius: 18,
                border: '1px solid var(--separator)',
                background: 'var(--background_2)',
                color: 'var(--font_1)',
                fontSize: 15,
                padding: '10px 14px',
                outline: 'none',
                resize: 'none',
                fontFamily: 'inherit',
                maxHeight: 120,
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  send()
                }
              }}
            />
            <button
              onClick={send}
              disabled={!canSend}
              style={{
                background: 'var(--primary)',
                color: 'var(--primary_text)',
                border: 'none',
                width: 40,
                height: 40,
                borderRadius: '50%',
                cursor: canSend ? 'pointer' : 'not-allowed',
                opacity: canSend ? 1 : 0.5,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
            <Send size={18} />
          </button>
          </div>
        </div>
        )
      })()}

      {/* Attachment lightbox — opens over the thread so the user stays in
          context instead of being navigated to a new tab. Navigates within
          the message's full attachment array via chevrons / keyboard /
          swipe. */}
      {lightbox && (
        <AttachmentLightbox
          attachments={lightbox.attachments}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  )
})

// ── Message stream rendering ─────────────────────────────────────────────
// Modern chat layout (Google Chat / Slack / Discord). Every message is
// left-aligned with avatar + name + timestamp + bubble. Consecutive messages
// from the same sender within ~3 minutes collapse: the second+ messages drop
// the avatar/name/timestamp and just show the bubble tucked under the first.

const GROUP_GAP_MS = 3 * 60_000

const AVATAR_PALETTE_THREAD = [
  'var(--ios-blue)',
  'var(--ios-green)',
  'var(--ios-orange)',
  'var(--ios-purple, #af52de)',
  'var(--ios-pink, #ff2d55)',
  'var(--ios-teal, #5ac8fa)',
  'var(--ios-indigo, #5856d6)',
]
function avatarColorFromName(name: string): string {
  const ch = (name.trim()[0]?.toUpperCase() || '?').charCodeAt(0)
  return AVATAR_PALETTE_THREAD[ch % AVATAR_PALETTE_THREAD.length]
}
function resolveAssetUrl(urlOrPath: string | null | undefined): string {
  if (!urlOrPath) return ''
  if (urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://')) {
    return urlOrPath
  }
  const base = process.env.NEXT_PUBLIC_SERVER_URL || ''
  const stripped = urlOrPath.replace(/^\/+/, '')
  return `${base}/${stripped.startsWith('assets/') ? stripped : `assets/${stripped}`}`
}
function profileImageUrl(path: string | null | undefined): string | null {
  if (!path) return null
  return resolveAssetUrl(path) || null
}

function senderNameFor(m: ConversationMessage, viewerId: string | null): string {
  if (m.senderType === 'admin') {
    // Always branded — never expose the operator's personal name to end
    // users. The backend already scrubs identity from the payload; this is
    // a hardening fallback so staff names can never render here.
    return 'MarkitMix Support'
  }
  if (m.senderType === 'ai_agent') return 'MarkitMix Assistant'
  if (m.senderType === 'system') return 'System'
  // user
  if (m.senderUserId && m.senderUserId === viewerId) {
    return m.sender?.displayName || m.sender?.name || 'You'
  }
  return m.sender?.displayName || m.sender?.name || 'User'
}

function senderKeyFor(m: ConversationMessage): string {
  if (m.senderUserId) return `u:${m.senderUserId}`
  return `t:${m.senderType}`
}

function fmtMessageTime(s: string): string {
  const d = new Date(s)
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

function fmtDayLabel(d: Date): string {
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  if (sameDay(d, today)) return 'Today'
  if (sameDay(d, yesterday)) return 'Yesterday'
  // Within last 7 days → weekday name
  const diffDays = Math.floor(
    (today.getTime() - d.getTime()) / (24 * 60 * 60 * 1000)
  )
  if (diffDays < 7) {
    return d.toLocaleDateString('en-US', { weekday: 'long' })
  }
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric',
  })
}

function renderMessageStream(
  messages: ConversationMessage[],
  viewerId: string | null,
  initialLastReadAt: string | null,
  messageStatuses: Record<string, 'sending' | 'sent' | 'seen' | 'failed'>,
  participantRoleByUserId: Record<string, 'buyer' | 'seller' | 'support' | 'ai_agent'>,
  onOpenAttachment: (list: MessageAttachment[], index: number) => void,
  onRetry: (tempId: string) => void
) {
  const items: React.ReactNode[] = []
  let lastSenderKey = ''
  let lastTimestamp = 0
  let lastDateKey = ''
  let unreadInserted = false
  const lastReadMs = initialLastReadAt
    ? new Date(initialLastReadAt).getTime()
    : 0

  messages.forEach((m) => {
    if (m.senderType === 'system') {
      // System messages render as a centered pill — never grouped.
      const d = new Date(m.createdAt)
      const dateKey = d.toDateString()
      if (dateKey !== lastDateKey) {
        items.push(<DateSeparator key={`d-${dateKey}`} date={d} />)
        lastDateKey = dateKey
      }
      items.push(<SystemPill key={m.id} m={m} />)
      lastSenderKey = ''
      lastTimestamp = 0
      return
    }

    // Vacation auto-replies render as a centered notice, never a bubble.
    if ((m.metadata as any)?.vacationAutoReply) {
      const d = new Date(m.createdAt)
      const dateKey = d.toDateString()
      if (dateKey !== lastDateKey) {
        items.push(<DateSeparator key={`d-${dateKey}`} date={d} />)
        lastDateKey = dateKey
      }
      items.push(<AutoReplyNotice key={m.id} m={m} />)
      lastSenderKey = ''
      lastTimestamp = 0
      return
    }

    const ts = new Date(m.createdAt).getTime()
    const d = new Date(m.createdAt)
    const dateKey = d.toDateString()

    // Day boundary → date separator + reset grouping.
    if (dateKey !== lastDateKey) {
      items.push(<DateSeparator key={`d-${dateKey}`} date={d} />)
      lastDateKey = dateKey
      lastSenderKey = ''
      lastTimestamp = 0
    }

    // Unread divider — first message after my last-read-at not from me.
    if (
      !unreadInserted &&
      lastReadMs &&
      ts > lastReadMs &&
      m.senderUserId !== viewerId
    ) {
      items.push(<UnreadDivider key="unread-divider" />)
      unreadInserted = true
      lastSenderKey = ''
      lastTimestamp = 0
    }

    const senderKey = senderKeyFor(m)
    const isContinuation =
      senderKey === lastSenderKey && ts - lastTimestamp < GROUP_GAP_MS

    items.push(
      <MessageRow
        key={m.id}
        m={m}
        viewerId={viewerId}
        isContinuation={isContinuation}
        status={messageStatuses[m.id]}
        senderRole={
          m.senderUserId ? participantRoleByUserId[m.senderUserId] : undefined
        }
        onOpenAttachment={onOpenAttachment}
        onRetry={onRetry}
      />
    )
    lastSenderKey = senderKey
    lastTimestamp = ts
  })

  return items
}

const DateSeparator = ({ date }: { date: Date }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '14px 16px 6px',
    }}
  >
    <div style={{ flex: 1, height: 1, background: 'var(--separator)' }} />
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--font_2)',
        letterSpacing: '0.02em',
      }}
    >
      {fmtDayLabel(date)}
    </span>
    <div style={{ flex: 1, height: 1, background: 'var(--separator)' }} />
  </div>
)

const UnreadDivider = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '8px 16px 4px',
    }}
  >
    <div
      style={{
        flex: 1,
        height: 1,
        background: 'color-mix(in srgb, var(--primary) 50%, transparent)',
      }}
    />
    <span
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: 'var(--primary)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}
    >
      New
    </span>
    <div
      style={{
        flex: 1,
        height: 1,
        background: 'color-mix(in srgb, var(--primary) 50%, transparent)',
      }}
    />
  </div>
)

const TypingPill = ({
  typers,
}: {
  typers: Record<string, { name: string | null; lastSeenAt: number }>
}) => {
  const names = Object.values(typers)
    .map((t) => t.name?.trim() || 'Someone')
  let label: string
  if (names.length === 0) return null
  if (names.length === 1) label = `${names[0]} is typing`
  else if (names.length === 2) label = `${names[0]} and ${names[1]} are typing`
  else label = 'Several people are typing'

  return (
    <div
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 16px 4px',
        fontSize: 12,
        color: 'var(--font_2)',
        background: 'var(--background_1, var(--background_2))',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        <TypingDot delay={0} />
        <TypingDot delay={150} />
        <TypingDot delay={300} />
      </span>
      <span style={{ fontWeight: 500 }}>{label}</span>
    </div>
  )
}

/**
 * Compact role chip rendered alongside a sender's name on a message row.
 * Multiple chips can sit side-by-side without a separator — that's the
 * whole point of using chips instead of a "(you · seller)" string.
 */
const RoleChip = ({
  label,
  tone,
}: {
  label: string
  tone: 'me' | 'seller'
}) => {
  const color =
    tone === 'me' ? 'var(--primary)' : 'var(--ios-green)'
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 11,
        fontWeight: 600,
        color,
        background: `color-mix(in srgb, ${color} 14%, transparent)`,
        padding: '1px 6px',
        borderRadius: 4,
        letterSpacing: '0.02em',
        textTransform: 'lowercase',
      }}
    >
      {label}
    </span>
  )
}

const TypingDot = ({ delay }: { delay: number }) => (
  <span
    style={{
      display: 'inline-block',
      width: 5,
      height: 5,
      borderRadius: '50%',
      background: 'var(--font_2)',
      opacity: 0.5,
      animation: 'mm-typing-bounce 1.2s infinite ease-in-out',
      animationDelay: `${delay}ms`,
    }}
  />
)

/** Vacation auto-reply as a centered system notice — third person with the
 *  seller's avatar, their note quoted beneath — so it never reads as a message
 *  the seller hand-typed. Mirrors the mobile treatment; messages from servers
 *  without the structured metadata fall back to the body text. */
const AutoReplyNotice = ({ m }: { m: ConversationMessage }) => {
  const meta = (m.metadata as any) || {}
  const label =
    typeof meta.awayUntilLabel === 'string' && meta.awayUntilLabel
      ? (meta.awayUntilLabel as string)
      : null
  const note = typeof meta.note === 'string' ? (meta.note as string).trim() : ''
  const name = m.sender?.displayName || m.sender?.name || 'The seller'
  const fallbackBody = (m.body || '').replace(/^Auto-reply:\s*/, '')
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        padding: '10px 24px',
        textAlign: 'center',
      }}
    >
      {label ? (
        <>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--font_2)',
            }}
          >
            <AvatarCircle
              url={profileImageUrl(m.sender?.profileImagePath)}
              initial={(name.trim()[0] || '?').toUpperCase()}
              bgColor={avatarColorFromName(name)}
              size={18}
              fontSize={10}
            />
            <span>
              {note
                ? `${name} is away until ${label}`
                : `${name} is away until ${label} and will reply when they're back`}
            </span>
          </div>
          {note ? (
            <div style={{ fontSize: 13, color: 'var(--font_2)', maxWidth: 480 }}>
              &ldquo;{note}&rdquo;
            </div>
          ) : null}
        </>
      ) : (
        <div style={{ fontSize: 13, color: 'var(--font_2)', maxWidth: 480 }}>
          {fallbackBody}
        </div>
      )}
    </div>
  )
}

const SystemPill = ({ m }: { m: ConversationMessage }) => {
  const meta = (m.metadata as any) || {}
  const event = meta.systemEvent as string | undefined
  const rating = typeof meta.rating === 'number' ? meta.rating : null

  // For rated events, render a star-row pill with the score so both the
  // customer and ops see the same record in the chat stream. Other events
  // use a plain label.
  if (event === 'rated' && rating != null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 16px' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: 'var(--font_2)',
            padding: '4px 12px',
            background: 'var(--background_3, var(--separator))',
            borderRadius: 999,
          }}
        >
          Rated{' '}
          <span aria-label={`${rating} out of 5`}>
            {[1, 2, 3, 4, 5].map((n) => (
              <span key={n} style={{ color: n <= rating ? 'var(--ios-yellow, #f5a623)' : 'var(--font_3)' }}>
                {n <= rating ? '★' : '☆'}
              </span>
            ))}
          </span>
        </span>
      </div>
    )
  }

  const label = (() => {
    if (m.body) return m.body
    switch (event) {
      case 'closed':
        return 'Conversation closed'
      case 'reopened':
        return 'Conversation reopened'
      case 'suggestion_auto_reply':
        return null // already rendered as a real message body
      default:
        return event || 'System'
    }
  })()

  if (!label) return null

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 16px' }}>
      <span
        style={{
          fontSize: 12,
          color: 'var(--font_2)',
          padding: '4px 12px',
          background: 'var(--background_3, var(--separator))',
          borderRadius: 999,
        }}
      >
        {label}
      </span>
    </div>
  )
}

const MessageRow = ({
  m,
  viewerId,
  isContinuation,
  status,
  senderRole,
  onOpenAttachment,
  onRetry,
}: {
  m: ConversationMessage
  viewerId: string | null
  isContinuation: boolean
  /** Status caption for this message ("Sending"/"Sent"/"Seen"/"Failed").
   *  Computed by the parent so at most one badge is shown per spot. */
  status?: 'sending' | 'sent' | 'seen' | 'failed'
  /** Role of the message's sender in this conversation — drives the small
   *  "(seller)" tag next to peer names. */
  senderRole?: 'buyer' | 'seller' | 'support' | 'ai_agent'
  onOpenAttachment: (list: MessageAttachment[], index: number) => void
  onRetry: (tempId: string) => void
}) => {
  const senderName = senderNameFor(m, viewerId)
  const isMe = viewerId
    ? m.senderUserId === viewerId
    : m.senderType === 'user'
  const isSupport = m.senderType === 'admin' || m.senderType === 'ai_agent'

  // Support messages always use the MarkitMix logo avatar — never an
  // operator's personal profile photo.
  const avatarUrl = isSupport ? null : profileImageUrl(m.sender?.profileImagePath)
  const avatarBg = isSupport
    ? 'var(--primary)'
    : avatarColorFromName(senderName)
  const initial = isSupport ? 'M' : (senderName.trim()[0] || '?').toUpperCase()

  const attachments = m.attachments || []
  // A message sent with a listing / order card carries the card's summary
  // in its body for older clients; render the card and only what was typed.
  const reference = parseMessageReference(m.metadata)
  const body = stripReferenceSummary(m.body, reference)
  const hasBody = body.trim().length > 0
  const hasAttachments = attachments.length > 0

  // Build the per-message order-number link target. Sellers jump to their
  // seller dashboard orders list (where they can act on shipping/payouts);
  // buyers go to their profile orders tab. Lang comes from the URL so the
  // user lands on a page that doesn't bounce through the i18n middleware.
  const pathname = usePathname() || ''
  const lang = pathname.split('/')[1] || 'en'
  const isSeller = !!AppStore.accountData?.isSeller
  const orderHrefFor = (n: string) =>
    isSeller
      ? `/${lang}/seller/orders?search=${encodeURIComponent(n)}`
      : `/${lang}/profile?tab=orders&search=${encodeURIComponent(n)}`

  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        padding: isContinuation ? '1px 16px' : '8px 16px 1px',
        alignItems: 'flex-start',
      }}
    >
      {/* Avatar slot — empty placeholder on continuation rows so bubbles align */}
      <div style={{ width: 36, flexShrink: 0 }}>
        {!isContinuation && (
          <AvatarCircle
            url={avatarUrl}
            initial={initial}
            bgColor={avatarBg}
            isSupport={isSupport}
            size={36}
            fontSize={14}
          />
        )}
      </div>

      {/* Message body column */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {!isContinuation && (
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
              flexWrap: 'wrap',
              marginBottom: 2,
            }}
          >
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--font_1)',
                letterSpacing: '-0.01em',
              }}
            >
              {senderName}
            </span>
            {/* Role tags — small pill chips, never joined by separators
                (per project convention). Each tag is its own span; both
                can appear side-by-side if the user is "you" AND the seller
                of the thread. Support / AI never get a role tag (the
                TEAM / AI badge already conveys it). */}
            {isMe && <RoleChip label="you" tone="me" />}
            {!isSupport && senderRole === 'seller' && (
              <RoleChip label="seller" tone="seller" />
            )}
            {isSupport && (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--primary)',
                  background: 'color-mix(in srgb, var(--primary) 14%, transparent)',
                  padding: '1px 6px',
                  borderRadius: 4,
                  letterSpacing: '0.02em',
                }}
              >
                {m.senderType === 'ai_agent' ? 'AI' : 'TEAM'}
              </span>
            )}
            <span
              style={{
                fontSize: 12,
                color: 'var(--font_3)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {fmtMessageTime(m.createdAt)}
            </span>
          </div>
        )}

        {reference && (
          <ReferenceCard reference={reference} lang={lang} viewerIsSeller={isSeller} />
        )}

        {hasAttachments && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                attachments.length === 1 ? '1fr' : 'repeat(2, minmax(0, 1fr))',
              gap: 4,
              maxWidth: 360,
              marginTop: hasBody ? 0 : 0,
            }}
          >
            {attachments.map((a, i) => (
              <BubbleAttachment
                key={`${a.assetId ?? a.url}-${i}`}
                a={a}
                onOpen={() => onOpenAttachment(attachments, i)}
              />
            ))}
          </div>
        )}

        {hasBody && (
          <div
            style={{
              alignSelf: 'flex-start',
              maxWidth: '85%',
              background: isMe
                ? 'color-mix(in srgb, var(--primary) 12%, var(--background_3, var(--separator)))'
                : 'var(--background_3, var(--separator))',
              color: 'var(--font_1)',
              padding: '8px 12px',
              borderRadius: 14,
              fontSize: 14,
              lineHeight: 1.45,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {linkifyOrderNumbers(body, orderHrefFor)}
          </div>
        )}
        {status && (
          <MessageStatusCaption
            status={status}
            messageId={m.id}
            onRetry={onRetry}
          />
        )}
      </div>
    </div>
  )
}

const MessageStatusCaption = ({
  status,
  messageId,
  onRetry,
}: {
  status: 'sending' | 'sent' | 'seen' | 'failed'
  messageId: string
  onRetry: (id: string) => void
}) => {
  if (status === 'failed') {
    return (
      <div
        style={{
          marginTop: 2,
          fontSize: 12,
          color: 'var(--ios-red)',
          fontWeight: 500,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span>Couldn&apos;t send</span>
        <span
          role="button"
          tabIndex={0}
          onClick={() => onRetry(messageId)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onRetry(messageId)
            }
          }}
          className="cursor-pointer select-none"
          style={{
            textDecoration: 'underline',
            color: 'var(--ios-red)',
          }}
        >
          Tap to retry
        </span>
      </div>
    )
  }
  if (status === 'sending') {
    return (
      <div
        aria-live="polite"
        style={{
          marginTop: 2,
          fontSize: 12,
          color: 'var(--font_3)',
          fontStyle: 'italic',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        Sending
        <span style={{ display: 'inline-flex', gap: 2 }}>
          <TypingDot delay={0} />
          <TypingDot delay={150} />
          <TypingDot delay={300} />
        </span>
      </div>
    )
  }
  // 'sent' / 'seen' — quiet gray caption
  return (
    <div
      style={{
        marginTop: 2,
        fontSize: 12,
        color: 'var(--font_3)',
        fontWeight: 500,
      }}
    >
      {status === 'seen' ? 'Seen' : 'Sent'}
    </div>
  )
}

/**
 * The listing, order, or bundle a message is about, rendered in the stream
 * as a card under the sender's name. One thread per pair means this card,
 * not a pinned header, is how both sides recall the specifics: it opens the
 * listing page, or the viewer's own orders filtered to that order.
 */
const ReferenceCard = ({
  reference,
  lang,
  viewerIsSeller,
}: {
  reference: MessageReference
  lang: string
  viewerIsSeller: boolean
}) => {
  const href = referenceHref(reference, { lang, viewerIsSeller })
  const thumbs = referenceThumbnails(reference)
  const facts = referenceFacts(reference)
  const prices = referencePriceFacts(reference)
  const title = referenceTitle(reference)
  const inner = (
    <>
      <ReferenceThumb paths={thumbs} isListing={isListingReference(reference)} size={52} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: 'var(--font_3)',
          }}
        >
          {referenceEyebrow(reference)}
        </span>
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--font_1)',
            lineHeight: 1.3,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {title}
        </span>
        {facts.length > 0 && (
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: '0 10px', fontSize: 12, color: 'var(--font_2)' }}>
            {facts.map((fact) => (
              <span key={fact} className="[overflow-wrap:anywhere]">{fact}</span>
            ))}
          </span>
        )}
        {prices.length > 0 && (
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: '0 10px', fontSize: 12, fontWeight: 500, color: 'var(--font_2)' }}>
            {prices.map((fact) => (
              <span key={fact}>{fact}</span>
            ))}
          </span>
        )}
      </div>
      {href && <ChevronRight size={16} style={{ color: 'var(--font_3)', flexShrink: 0 }} />}
    </>
  )
  const style: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    maxWidth: 360,
    padding: 10,
    borderRadius: 14,
    border: '1px solid var(--separator)',
    background: 'var(--background_2)',
    color: 'inherit',
    textDecoration: 'none',
    boxSizing: 'border-box',
  }
  if (!href) return <div style={style}>{inner}</div>
  return (
    <Link href={href} aria-label={`View ${title}`} style={style}>
      {inner}
    </Link>
  )
}

/** The card going out with the message being composed, with a way to drop it. */
const PendingReferenceChip = ({
  reference,
  onRemove,
}: {
  reference: MessageReference
  onRemove: () => void
}) => {
  const title = referenceTitle(reference)
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 8px 8px 10px',
        borderRadius: 12,
        border: '1px solid var(--separator)',
        background: 'var(--background_2)',
      }}
    >
      <ReferenceThumb
        paths={referenceThumbnails(reference)}
        isListing={isListingReference(reference)}
        size={36}
      />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: 'var(--font_3)',
          }}
        >
          Attached to message
        </span>
        <span
          title={title}
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--font_1)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </span>
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${title} from this message`}
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          border: 'none',
          background: 'transparent',
          color: 'var(--font_2)',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          padding: 0,
        }}
      >
        <XIcon size={15} strokeWidth={2} />
      </button>
    </div>
  )
}

/** Square item photo for a reference card, with a kind icon when there is none. */
const ReferenceThumb = ({
  paths,
  isListing,
  size,
}: {
  paths: string[]
  isListing: boolean
  size: number
}) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: 10,
      overflow: 'hidden',
      flexShrink: 0,
      background: 'var(--background_3, var(--separator))',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'var(--font_3)',
    }}
  >
    {paths[0] ? (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={resolveAssetUrl(paths[0])}
        alt=""
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    ) : isListing ? (
      <Tag size={Math.round(size * 0.4)} strokeWidth={1.75} />
    ) : (
      <Package size={Math.round(size * 0.4)} strokeWidth={1.75} />
    )}
  </div>
)

/** Square thumb chip shown above the composer for each pending upload. */
const ReplyAttachmentChip = ({
  a,
  onRemove,
}: {
  a: MessageAttachment
  onRemove: () => void
}) => {
  const isImage = (a.mime || '').startsWith('image/')
  const isVideo = (a.mime || '').startsWith('video/')
  return (
    <div
      style={{
        position: 'relative',
        aspectRatio: '1 / 1',
        borderRadius: 10,
        overflow: 'hidden',
        background: 'var(--background_3, var(--separator))',
        border: '1px solid var(--separator)',
      }}
    >
      {isImage ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={resolveAssetUrl(a.url)}
          alt={a.filename}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : isVideo ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: 'var(--font_2)',
            gap: 2,
            padding: 4,
          }}
        >
          <Film size={16} strokeWidth={1.5} />
          <span
            style={{
              fontSize: 10,
              textAlign: 'center',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {a.filename}
          </span>
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: 'var(--font_2)',
            gap: 3,
            padding: 5,
          }}
        >
          <FileIcon size={18} />
          <span
            title={a.filename}
            style={{
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 10,
            }}
          >
            {a.filename}
          </span>
        </div>
      )}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${a.filename}`}
        style={{
          position: 'absolute',
          top: 3,
          right: 3,
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: 'rgba(0, 0, 0, 0.65)',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
      >
        <XIcon size={11} strokeWidth={2.5} />
      </button>
    </div>
  )
}

const BubbleAttachment = ({
  a,
  onOpen,
}: {
  a: MessageAttachment
  onOpen: () => void
}) => {
  const isImage = (a.mime || '').startsWith('image/')
  const isVideo = (a.mime || '').startsWith('video/')
  const isKlipy = a.provider === 'klipy'
  const url = resolveAssetUrl(a.url)

  if (isImage) {
    return (
      <div
        role="button"
        tabIndex={0}
        aria-label={`Open ${a.filename}`}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onOpen()
          }
        }}
        className="cursor-pointer select-none"
        style={{
          position: 'relative',
          display: 'block',
          aspectRatio: '1 / 1',
          borderRadius: 12,
          overflow: 'hidden',
          background: 'var(--background_3, var(--separator))',
        }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={url}
          alt={a.filename}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
        {isKlipy && (
          <span
            style={{
              position: 'absolute',
              right: 6,
              bottom: 6,
              padding: '3px 6px',
              borderRadius: 999,
              color: '#fff',
              background: 'rgba(0, 0, 0, 0.68)',
              fontSize: 9,
              fontWeight: 700,
              lineHeight: 1.2,
              letterSpacing: '0.02em',
              pointerEvents: 'none',
            }}
          >
            Powered by KLIPY
          </span>
        )}
      </div>
    )
  }
  if (isVideo) {
    // Thumbnail-style preview: show first frame with a play overlay. Tapping
    // anywhere opens the lightbox where the video plays with full controls.
    // (Native <video controls> in the bubble fights with tap-to-open and
    // takes more vertical space than a chat row should.)
    return (
      <div
        role="button"
        tabIndex={0}
        aria-label={`Play ${a.filename}`}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onOpen()
          }
        }}
        className="cursor-pointer select-none"
        style={{
          position: 'relative',
          aspectRatio: '16 / 9',
          borderRadius: 12,
          overflow: 'hidden',
          background: 'black',
        }}
      >
        <video
          src={url}
          muted
          playsInline
          preload="metadata"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            pointerEvents: 'none',
          }}
        />
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: 'rgba(0,0,0,0.55)',
            color: 'white',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
          }}
        >
          ▶
        </span>
      </div>
    )
  }
  // Documents and archives use the browser's native preview/download path.
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        borderRadius: 10,
        background: 'var(--background_2)',
        border: '1px solid var(--separator)',
        color: 'var(--font_1)',
        fontSize: 13,
        textDecoration: 'none',
      }}
    >
      <FileIcon size={14} />
      <span style={{ wordBreak: 'break-all' }}>{a.filename}</span>
    </a>
  )
}

/**
 * Fullscreen overlay for viewing a message's attachments. Supports prev/next
 * navigation across the message's attachment array via side chevrons,
 * keyboard arrows, and horizontal swipe (mobile). Closes on ESC,
 * click-outside the media, or the top-right X button.
 */
const AttachmentLightbox = ({
  attachments,
  initialIndex,
  onClose,
}: {
  attachments: MessageAttachment[]
  initialIndex: number
  onClose: () => void
}) => {
  const [index, setIndex] = useState(initialIndex)
  const total = attachments.length
  const a = attachments[index]
  const url = resolveAssetUrl(a.url)
  const isImage = (a.mime || '').startsWith('image/')
  const isVideo = (a.mime || '').startsWith('video/')
  const hasPrev = index > 0
  const hasNext = index < total - 1

  const goPrev = useCallback(() => {
    setIndex((i) => (i > 0 ? i - 1 : i))
  }, [])
  const goNext = useCallback(() => {
    setIndex((i) => (i < total - 1 ? i + 1 : i))
  }, [total])

  useEffect(() => {
    document.body.classList.add('no-scroll')
    return () => document.body.classList.remove('no-scroll')
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowLeft') goPrev()
      else if (e.key === 'ArrowRight') goNext()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, goPrev, goNext])

  // Touch swipe — horizontal threshold of 50px triggers nav.
  const touchStartXRef = useRef<number | null>(null)
  const touchStartYRef = useRef<number | null>(null)
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX
    touchStartYRef.current = e.touches[0].clientY
  }
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartXRef.current == null || touchStartYRef.current == null) return
    const dx = e.changedTouches[0].clientX - touchStartXRef.current
    const dy = e.changedTouches[0].clientY - touchStartYRef.current
    touchStartXRef.current = null
    touchStartYRef.current = null
    // Ignore mostly-vertical gestures (let pinch-zoom and scroll be).
    if (Math.abs(dx) < 50 || Math.abs(dy) > Math.abs(dx)) return
    if (dx > 0) goPrev()
    else goNext()
  }

  return (
    <div
      role="dialog"
      aria-modal
      aria-label={a.filename}
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0, 0, 0, 0.86)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '56px 16px 16px',
      }}
    >
      {/* Top bar: counter + filename + actions */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 14px',
          background:
            'linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0))',
        }}
      >
        {total > 1 && (
          <span
            style={{
              color: 'white',
              fontSize: 13,
              fontWeight: 600,
              opacity: 0.9,
              flexShrink: 0,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {index + 1} of {total}
          </span>
        )}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            color: 'white',
            fontSize: 14,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {a.filename}
        </div>
        <a
          href={url}
          download={a.filename}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open in new tab"
          title="Open in new tab"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: 8,
            color: 'white',
            textDecoration: 'none',
            opacity: 0.85,
          }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.opacity = '1')}
          onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.opacity = '0.85')}
        >
          <ExternalLink size={18} />
        </a>
        <div
          role="button"
          tabIndex={0}
          aria-label="Close"
          onClick={onClose}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onClose()
            }
          }}
          className="cursor-pointer select-none"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: 8,
            color: 'white',
            opacity: 0.85,
          }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.opacity = '1')}
          onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.opacity = '0.85')}
        >
          <XIcon size={20} />
        </div>
      </div>

      {/* Prev chevron */}
      {hasPrev && (
        <LightboxChevron direction="prev" onClick={goPrev} />
      )}
      {hasNext && (
        <LightboxChevron direction="next" onClick={goNext} />
      )}

      {/* Content — stopPropagation so taps inside the media don't dismiss.
          key={index} forces remount of the video element on swap, which
          aborts the previous src + autoplays the new one cleanly. */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isImage && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            key={index}
            src={url}
            alt={a.filename}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              display: 'block',
              borderRadius: 8,
            }}
          />
        )}
        {isVideo && (
          <video
            key={index}
            src={url}
            controls
            autoPlay
            playsInline
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              display: 'block',
              borderRadius: 8,
              background: 'black',
            }}
          />
        )}
        {!isImage && !isVideo && (
          <div
            style={{
              color: 'white',
              fontSize: 14,
              textAlign: 'center',
              padding: 24,
            }}
          >
            <FileIcon size={48} style={{ opacity: 0.6, marginBottom: 8 }} />
            <div>{a.filename}</div>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'var(--ios-blue, #0A84FF)',
                marginTop: 12,
                display: 'inline-block',
              }}
            >
              Download
            </a>
          </div>
        )}
      </div>
    </div>
  )
}

const LightboxChevron = ({
  direction,
  onClick,
}: {
  direction: 'prev' | 'next'
  onClick: () => void
}) => {
  const Icon = direction === 'prev' ? ChevronLeft : ChevronRight
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={direction === 'prev' ? 'Previous' : 'Next'}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      className="cursor-pointer select-none"
      style={{
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        ...(direction === 'prev' ? { left: 12 } : { right: 12 }),
        width: 44,
        height: 44,
        borderRadius: '50%',
        background: 'rgba(0, 0, 0, 0.55)',
        color: 'white',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0.85,
        transition: 'opacity 120ms, background 120ms',
      }}
      onMouseEnter={(e) => {
        ;(e.currentTarget as HTMLElement).style.opacity = '1'
        ;(e.currentTarget as HTMLElement).style.background = 'rgba(0, 0, 0, 0.75)'
      }}
      onMouseLeave={(e) => {
        ;(e.currentTarget as HTMLElement).style.opacity = '0.85'
        ;(e.currentTarget as HTMLElement).style.background = 'rgba(0, 0, 0, 0.55)'
      }}
    >
      <Icon size={22} />
    </div>
  )
}

/**
 * CSAT prompt rendered as a card inside the thread (not a modal). Replaces
 * the previous full-screen overlay so the survey feels like a message rather
 * than an interruption. Disappears once the user rates or dismisses, both of
 * which flip server state and re-fetch the conversation.
 */
const InlineRatingCard = ({
  onSubmit,
  onDismiss,
}: {
  onSubmit: (rating: number, comment?: string) => Promise<void>
  onDismiss: () => Promise<void>
}) => {
  const [rating, setRating] = useState(0)
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)

  return (
    <div
      style={{
        margin: '12px 16px',
        padding: 14,
        borderRadius: 14,
        background: 'var(--background_2)',
        border: '1px solid var(--separator)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--font_1)' }}>
          How did we do?
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss rating prompt"
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--font_3)',
            cursor: 'pointer',
            fontSize: 12,
            padding: 0,
          }}
        >
          Dismiss
        </button>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setRating(n)}
            aria-label={`${n} of 5`}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: 26,
              color: n <= rating ? 'var(--ios-yellow, #f5a623)' : 'var(--font_3)',
              padding: 0,
              lineHeight: 1,
            }}
          >
            {n <= rating ? '★' : '☆'}
          </button>
        ))}
      </div>
      {rating > 0 && (
        <>
          <textarea
            placeholder="Anything we could do better? (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            style={{
              borderRadius: 10,
              border: '1px solid var(--separator)',
              background: 'var(--background_1, var(--background_2))',
              color: 'var(--font_1)',
              fontSize: 14,
              padding: 10,
              outline: 'none',
              resize: 'vertical',
              fontFamily: 'inherit',
            }}
          />
          <button
            type="button"
            onClick={async () => {
              if (rating === 0 || submitting) return
              setSubmitting(true)
              try {
                await onSubmit(rating, comment.trim() || undefined)
              } finally {
                setSubmitting(false)
              }
            }}
            disabled={submitting}
            style={{
              alignSelf: 'flex-end',
              height: 36,
              padding: '0 16px',
              borderRadius: 999,
              border: 'none',
              background: 'var(--primary)',
              color: 'var(--primary_text)',
              fontSize: 14,
              fontWeight: 500,
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.6 : 1,
            }}
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </>
      )}
    </div>
  )
}
